import {
  ActingAsBanner,
  AppSwitcher,
  BrandMark,
  Button,
  type CommandItem,
  CommandPalette,
  FeedbackButton,
  AppShell as LibraryAppShell,
  type NavLinkProps,
  type NavNode,
  orderNavItems,
  PoweredByOlytic,
  SetupIcon,
  type Shortcut,
  ShortcutSheet,
  Tooltip,
  TooltipProvider,
  RecordPreviewProvider,
  useCommandPalette,
  useShortcuts,
} from '@olytic/foundation';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import olyticMark from './assets/olytic-mark-reverse.svg';
import { Assistant } from './assistant/Assistant';
import { canUseAssistant } from './assistant/conversation';
import { accessOf, type CurrentUser } from './auth/api';
import { useCurrentUser } from './auth/AuthProvider';
import { authenticatedFetch } from './auth/authClient';
import { exitViewAs } from './auth/viewAs';
import { submitFeedback } from './feedbackApi';
import { objects } from './config/objects';
import { activeKeyFor, DELIVERY_HOME_ROUTE, HOME_ROUTE, isSetupPath, REPORTS_ROUTE, SETUP_ROUTES } from './navigation';
import { AccountSizeHost } from './objects/accountSize';
import { createLookupRecord, LookupCreateHost } from './objects/lookupCreate';
import { loadRecentLookups } from './objects/lookupOptions';
import { recordPreviewLoader } from './objects/recordPreview';
import { listPath, recordPath } from './objects/routes';
import { lookupServices } from './objects/writes';
import { PageActionsProvider, usePageActionItems } from './pageActions';
import { useMyProfile } from './profile/ProfileProvider';
import { YourUserMenu } from './profile/YourUserMenu';
import { useRecentRecords } from './search/recentRecords';
import { useRecordSearch } from './search/recordSearch';
import { LogTimeProvider } from './work/logTime';
import { TopBarTimer } from './work/TopBarTimer';
import {
  type AppDef,
  appsFor,
  commandEntries,
  currentAppOf,
  loadCurrentApp,
  mayOpenTab,
  saveCurrentApp,
  setupNavigation,
  type TabDef,
  tabByKey,
  TABS_ROUTE,
  tabsFor,
  tabsOfApp,
  temporaryTab,
} from './tabs';

const PRODUCT_NAME = 'Mothership';

/** `g` then a key goes to a page. `tab` is the page's tab, which decides who may use it. */
const GO_TO: readonly { key: string; label: string; href: string; tab?: string }[] = [
  { key: 'h', label: 'Sales Pulse', href: HOME_ROUTE, tab: 'sales-pulse' },
  { key: 'd', label: 'My Delivery', href: DELIVERY_HOME_ROUTE, tab: 'delivery-home' },
  { key: 'w', label: 'Work', href: '/work', tab: 'work' },
  { key: 'a', label: 'Accounts', href: listPath('accounts'), tab: 'accounts' },
  { key: 'c', label: 'Contacts', href: listPath('contacts'), tab: 'contacts' },
  { key: 'o', label: 'Opportunities', href: listPath('opportunities'), tab: 'opportunities' },
  { key: 'p', label: 'Projects', href: listPath('projects'), tab: 'projects' },
  { key: 'm', label: 'Meetings', href: listPath('meetings'), tab: 'meetings' },
  { key: 'l', label: 'Leads', href: listPath('leads'), tab: 'leads' },
  { key: 'r', label: 'Reports', href: REPORTS_ROUTE, tab: 'reports' },
  { key: 's', label: 'Setup', href: SETUP_ROUTES.home },
];

/** The objects the palette offers to create: "New account" opens that list's New dialog. */
const CREATABLE = ['accounts', 'contacts', 'opportunities', 'projects', 'leads', 'meetings', 'time-entries', 'quotes', 'assets', 'products'] as const;

/**
 * The shell layout. `true`: the rail runs the full height, headed by the app
 * launcher as the parent of the current app's tabs; a dark bar beside it holds
 * the large Mothership lockup, the search, Setup and the account menu.
 * `false`: the compact brand heads the rail and a light top bar holds the
 * search and actions. One switch; nothing else changes.
 */
const SHELL_BAND = true;

function navNode(tab: TabDef): NavNode {
  return { key: tab.key, label: tab.label, icon: tab.icon, href: tab.href };
}

/**
 * The main rail: the current app's tabs, flat, in the order this person
 * dragged them into, else the app's. Tabs added since go at the end.
 */
function appNavigation(app: AppDef, saved: readonly string[] | undefined): NavNode[] {
  return orderNavItems(tabsOfApp(app).map(navNode), saved);
}

/** Setup sits in the app launcher like an app, after the others. */
const SETUP_APP = { key: 'setup', label: 'Setup', icon: <SetupIcon />, description: 'Users, objects and AI management' };

const RouterLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function RouterLink(
  { href, ...props },
  ref,
) {
  return <Link ref={ref} to={href} {...props} />;
});

/** The rail's brand, the same in every mode: the Olytic mark and the product name. */
function Brand({ home }: { home: string }): JSX.Element {
  return (
    <Link
      aria-label={`${PRODUCT_NAME} home`}
      className="flex min-w-0 items-center gap-2.5 no-underline"
      to={home}
    >
      <img alt="" height={24} src={olyticMark} width={34} />
      <span className="truncate text-sm font-strong tracking-tight text-[var(--nav-fg-strong)]">
        {PRODUCT_NAME}
      </span>
    </Link>
  );
}

/** The band's brand, before the search: the Olytic mark and MOTHERSHIP in tracked capitals. */
function BandBrand({ home }: { home: string }): JSX.Element {
  return (
    <Link aria-label={`${PRODUCT_NAME} home`} className="flex min-w-0 no-underline" to={home}>
      <BrandMark
        mark={<img alt="" height={28} src={olyticMark} width={40} />}
        name={PRODUCT_NAME}
        variant="lockup"
      />
    </Link>
  );
}

/**
 * Setup, beside the account menu: a filled circle the avatar's size. Inside
 * setup it turns into the way back: the current app's icon, returning to the
 * page you left.
 */
function SetupButton({ current, app, backTo }: { current: boolean; app: AppDef; backTo: string }): JSX.Element {
  const label = current ? `Back to ${app.label}` : 'Setup';
  return (
    <Tooltip label={label} side="bottom">
      <Button
        asChild
        // On the band the light --fill-subtle would glare; --surface-hover is
        // remapped to the band's own wash there.
        className={`size-8 rounded-full ring-1 ring-inset [&_svg]:size-[18px] ${SHELL_BAND ? 'bg-[var(--surface-hover)]' : 'bg-[var(--fill-subtle)]'} ${current ? 'text-[var(--fg-strong)] ring-[var(--fg-muted)]' : 'ring-[var(--border-hover)]'}`}
        size="icon"
        variant="subtle"
      >
        <Link aria-label={label} to={current ? backTo : SETUP_ROUTES.home}>
          {current ? app.icon : <SetupIcon />}
        </Link>
      </Button>
    </Tooltip>
  );
}

/**
 * Log in as user: while the server says an admin is viewing as someone, a banner above everything
 * with Exit. At the session's end the app reloads; the server refuses the session from then anyway.
 */
function ViewAsBanner({ user }: { user: CurrentUser }): JSX.Element | null {
  const by = user.impersonatedBy;
  useEffect(() => {
    if (!by) return undefined;
    const timer = window.setTimeout(() => window.location.assign('/'), Math.max(0, Date.parse(by.expiresAt) - Date.now()) + 1000);
    return () => window.clearTimeout(timer);
  }, [by]);
  if (!by) return null;
  const ends = new Date(by.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return (
    <ActingAsBanner
      message={<>Viewing as <strong>{user.name}</strong></>}
      detail={`Anything you do is recorded as ${by.name} · ends at ${ends}`}
      onExit={() => void exitViewAs()}
    />
  );
}

/** The chrome every page shares: the library shell with Ops navigation, search, account menu and assistant. */
// Every lookup editor suggests recently viewed records and offers "+ New".
lookupServices.recent = loadRecentLookups;
lookupServices.create = createLookupRecord;
lookupServices.singularOf = (object) => objects[object]?.singular.toLowerCase() ?? 'record';
const loadRecordPreview = recordPreviewLoader(authenticatedFetch);

export function AppShell(): JSX.Element {
  // Pages offer their own palette actions (pageActions.tsx) and Log time's dialog, so the providers wrap the shell.
  return (
    <PageActionsProvider>
      <LogTimeProvider>
        <Shell />
      </LogTimeProvider>
    </PageActionsProvider>
  );
}

function Shell(): JSX.Element {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const palette = useCommandPalette();
  const setup = isSetupPath(pathname);
  const user = useCurrentUser();
  const access = accessOf(user);
  const staff = access === 'all';
  const home = staff ? HOME_ROUTE : DELIVERY_HOME_ROUTE;
  const offered = useMemo(() => appsFor(access), [access]);
  // The current app only changes when someone switches: opening a tab from
  // another app (launcher, palette, All tabs, a link) keeps the rail where it
  // was, and shows that tab as temporary while it is open.
  const [appKey, setAppKey] = useState(loadCurrentApp);
  const app = currentAppOf(appKey, offered);
  const activeKey = activeKeyFor(pathname);
  // Switching keeps the page when the new app has it, else opens the app's first tab.
  const switchApp = useCallback(
    (key: string) => {
      const next = offered.find((a) => a.key === key);
      if (!next) return;
      setAppKey(key);
      saveCurrentApp(key);
      if (setup || !next.tabs.includes(activeKey)) navigate(tabsOfApp(next)[0]?.href ?? home);
    },
    [activeKey, home, navigate, offered, setup],
  );
  const { profile, saveTabOrder } = useMyProfile();
  const savedOrder = profile?.tabOrder[app.key];
  const mainNavigation = useMemo(() => appNavigation(app, savedOrder), [app, savedOrder]);
  // Setup's pages this person may open: an Admin-only one only for an Admin, on any Setup URL.
  const setupItems = useMemo(() => setupNavigation(user.role), [user.role]);
  // A failed save puts the old order back, which is the message.
  const reorderTabs = useCallback(
    (keys: string[]) => void saveTabOrder(app.key, keys).catch(() => undefined),
    [app.key, saveTabOrder],
  );
  const temporary = setup ? undefined : temporaryTab(app, activeKey, access);
  const launcherTabs = useMemo(
    () => tabsFor(access).map((t) => ({ key: t.key, label: t.label, icon: t.icon, href: t.href })),
    [access],
  );
  // The last page outside setup, so the Setup button can lead back to it.
  const lastAppPath = useRef(home);
  useEffect(() => {
    if (!setup) lastAppPath.current = `${pathname}${search}`;
  }, [pathname, search, setup]);

  const records = useRecordSearch(authenticatedFetch);
  const recent = useRecentRecords(authenticatedFetch, palette.open);
  const pageActions = usePageActionItems();

  const goTo = useMemo<Shortcut[]>(
    () =>
      GO_TO.filter((g) => {
        const tab = g.tab ? tabByKey(g.tab) : undefined;
        return tab ? mayOpenTab(tab, access) : staff;
      }).map((g) => ({ keys: `g ${g.key}`, handler: () => navigate(g.href), description: g.label, group: 'Go to' })),
    [access, navigate, staff],
  );
  useShortcuts(goTo);

  const commands = useMemo<CommandItem[]>(
    () => [
      // Shown before anything is typed, and filtered by name as you type.
      ...recent.map((r) => ({
        id: `recent:${r.object}:${r.id}`,
        label: r.label,
        group: 'Recent',
        icon: objects[r.object].icon,
        hint: objects[r.object].singular,
        keywords: objects[r.object].plural,
        onSelect: () => navigate(recordPath(r.object, r.id)),
      })),
      ...pageActions,
      ...records.results.filter((r) => !recent.some((v) => v.object === r.object && v.id === r.id)).map((r) => ({
        id: `record:${r.object}:${r.id}`,
        label: r.label,
        group: objects[r.object].plural,
        icon: objects[r.object].icon,
        ...(r.detail ? { hint: r.detail } : {}),
        external: true,
        onSelect: () => navigate(recordPath(r.object, r.id)),
      })),
      ...(staff ? CREATABLE : []).map((key) => ({
        id: `new:${key}`,
        label: `New ${objects[key].singular.toLowerCase()}`,
        group: 'Create',
        icon: objects[key].icon,
        keywords: `create add ${objects[key].plural}`,
        onSelect: () => navigate(`${listPath(key)}?new=1`),
      })),
      ...commandEntries(access, user.role).map((entry) => ({
        id: entry.id,
        label: entry.label,
        group: entry.group,
        icon: entry.icon,
        ...(entry.keywords ? { keywords: entry.keywords } : {}),
        onSelect: () => {
          if (entry.appKey) switchApp(entry.appKey);
          else if (entry.href) navigate(entry.href);
        },
      })),
    ],
    [access, navigate, pageActions, recent, records.results, staff, switchApp, user.role],
  );

  return (
    <TooltipProvider>
      <LibraryAppShell
        // Remount on switching modes, so each nav opens its own groups.
        key={setup ? 'setup' : 'main'}
        activeKey={activeKey}
        topBar={SHELL_BAND ? 'band' : 'bar'}
        brandPlacement="band"
        banner={<ViewAsBanner user={user} />}
        bandBrand={<BandBrand home={home} />}
        brand={<Brand home={home} />}
        brandMark={
          <Link aria-label={`${PRODUCT_NAME} home`} to={home}>
            <img alt="" height={26} src={olyticMark} width={36} />
          </Link>
        }
        railHeader={
          <AppSwitcher
            activeKey={setup ? SETUP_APP.key : app.key}
            apps={[
              ...offered.map((a) => ({ key: a.key, label: a.label, icon: a.icon, description: a.description })),
              ...(staff ? [SETUP_APP] : []),
            ]}
            tabs={launcherTabs}
            footer={{ label: 'All tabs', href: TABS_ROUTE, hint: '⌘K' }}
            linkComponent={RouterLink}
            onSelect={(key) => {
              if (key === SETUP_APP.key) navigate(SETUP_ROUTES.home);
              else switchApp(key);
            }}
          />
        }
        defaultOpenGroups="all"
        // The current app (or Setup) is the parent row; its tabs hang under it.
        nestItemsUnderRailHeader={SHELL_BAND}
        // The assistant launcher floats over the bottom-right corner.
        floatingCorner
        items={setup ? setupItems : mainNavigation}
        // Each person orders each app's tabs by dragging them; Setup's stay as they are.
        {...(setup || profile === null ? {} : { onReorder: reorderTabs })}
        temporaryItems={temporary ? [navNode(temporary)] : []}
        linkComponent={RouterLink}
        onSearchOpen={() => palette.setOpen(true)}
        searchHint={
          <kbd className="oly-collabel rounded-[var(--radius-sm)] border border-[var(--border)] px-1 py-0.5">⌘K</kbd>
        }
        sidebarFooter={<PoweredByOlytic />}
        topBarActions={
          <>
            <TopBarTimer />
            <FeedbackButton productName={PRODUCT_NAME} onSubmit={(body) => submitFeedback(authenticatedFetch, body)} />
            {staff ? <SetupButton app={app} backTo={lastAppPath.current} current={setup} /> : null}
            <YourUserMenu />
          </>
        }
      >
        {/* Lookup links open a card of their record when hovered. */}
        <RecordPreviewProvider load={loadRecordPreview}>
          <Outlet />
        </RecordPreviewProvider>
      </LibraryAppShell>
      <CommandPalette
        items={commands}
        open={palette.open}
        onOpenChange={palette.setOpen}
        onQueryChange={records.search}
        loading={records.loading}
        placeholder="Search records, pages and actions…"
        emptyLabel="Nothing matches"
      />
      <ShortcutSheet extra={[{ keys: '⌘K', handler: () => palette.setOpen(true), description: 'Search and jump', group: 'General' }]} />
      {/* Only for roles the assistant's route answers (POST /api/assistant/messages). */}
      {canUseAssistant(user) ? <Assistant fetcher={authenticatedFetch} /> : null}
      <LookupCreateHost />
      <AccountSizeHost />
    </TooltipProvider>
  );
}
